import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { codeSnippetAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CodeSnippetEditor from '../components/CodeSnippetEditor';

const CodeSnippets = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Editor modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [editingSnippet, setEditingSnippet] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingSnippet, setDeletingSnippet] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [pagination, setPagination] = useState(null);
  
  // Statistics
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchSnippets();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    fetchSnippets();
    fetchStatistics();
  }, [currentPage, sortBy, sortOrder, languageFilter, difficultyFilter, statusFilter, itemsPerPage]);

 const fetchSnippets = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder
      };

      if (searchQuery) params.search = searchQuery;
      if (languageFilter) params.programmingLanguage = languageFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      if (statusFilter) params.isPublished = statusFilter;

      const response = await codeSnippetAPI.getAllSnippets(params);
      setSnippets(response.data.snippets);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch snippets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await codeSnippetAPI.getStatistics();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  const handleCreateSnippet = async (data) => {
    try {
      console.log('Creating snippet with data:', data);
      const response = await codeSnippetAPI.createSnippet(data);
      console.log('Create response:', response.data);
      setSuccess(response.data.message);
      fetchSnippets();
      fetchStatistics();
      setEditorOpen(false);
    } catch (err) {
      console.error('Error creating snippet:', err);
      console.error('Error response:', err.response?.data);
      throw err;
    }
  };

  const handleUpdateSnippet = async (data) => {
    try {
      const response = await codeSnippetAPI.updateSnippet(editingSnippet._id, data);
      setSuccess('Snippet updated successfully');
      fetchSnippets();
      setEditorOpen(false);
      setEditingSnippet(null);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteSnippet = async () => {
    try {
      await codeSnippetAPI.deleteSnippet(deletingSnippet._id);
      setSuccess('Snippet deleted successfully');
      fetchSnippets();
      fetchStatistics();
      setDeleteDialogOpen(false);
      setDeletingSnippet(null);
    } catch (err) {
      setError('Failed to delete snippet');
    }
  };

  const handleRegenerateExplanations = async (snippetId) => {
    try {
      setSuccess('Regenerating explanations...');
      const response = await codeSnippetAPI.regenerateExplanations(snippetId);
      setSuccess('Explanations regenerated successfully');
      fetchSnippets();
    } catch (err) {
      setError('Failed to regenerate explanations');
    }
  };

  const handleViewSnippet = (snippet) => {
    navigate(`/code-snippets/${snippet._id}`);
  };

  const handleEditSnippet = (snippet) => {
    setEditingSnippet(snippet);
    setEditorMode('edit');
    setEditorOpen(true);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortOrderToggle = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const openCreateModal = () => {
    setEditingSnippet(null);
    setEditorMode('create');
    setEditorOpen(true);
  };

  const difficultyColors = {
    beginner: '#48bb78',
    intermediate: '#fbbf24',
    advanced: '#f97316',
    expert: '#ef4444'
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="container">
        <div className="card">
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <p style={{ color: '#856404', fontSize: '16px', margin: 0 }}>⚠️ Admin access required</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>💻 Code Snippets</h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            Manage and explain code snippets with AI-powered analysis
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <button className="btn btn-primary" onClick={openCreateModal}>
            + Create New Snippet
          </button>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px', 
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px'
          }}>
            <div>
              <div style={{ color: '#718096', fontSize: '12px', marginBottom: '4px' }}>Total Snippets</div>
              <div style={{ color: '#2d3748', fontSize: '24px', fontWeight: '600' }}>{stats.totalSnippets}</div>
            </div>
            <div>
              <div style={{ color: '#718096', fontSize: '12px', marginBottom: '4px' }}>Published</div>
              <div style={{ color: '#48bb78', fontSize: '24px', fontWeight: '600' }}>{stats.publishedSnippets}</div>
            </div>
            <div>
              <div style={{ color: '#718096', fontSize: '12px', marginBottom: '4px' }}>Languages</div>
              <div style={{ color: '#2d3748', fontSize: '24px', fontWeight: '600' }}>{stats.languageStats?.length || 0}</div>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: '#fff5f5',
            border: '1px solid #feb2b2',
            borderRadius: '8px',
            color: '#c53030',
            fontSize: '13px'
          }}>
            {error}
            <button
              onClick={() => setError('')}
              style={{
                float: 'right',
                background: 'none',
                border: 'none',
                color: '#c53030',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              ×
            </button>
          </div>
        )}
        {success && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: '#f0fff4',
            border: '1px solid #9ae6b4',
            borderRadius: '8px',
            color: '#22543d',
            fontSize: '13px'
          }}>
            {success}
            <button
              onClick={() => setSuccess('')}
              style={{
                float: 'right',
                background: 'none',
                border: 'none',
                color: '#22543d',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Search and Filter Controls */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '12px', 
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f7fafc',
          borderRadius: '8px'
        }}>
          <input
            type="text"
            placeholder="🔍 Search snippets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px'
            }}
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="createdAt">Date Created</option>
            <option value="name">Name</option>
            <option value="programmingLanguage">Language</option>
          </select>

          <button
            onClick={handleSortOrderToggle}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
          </button>

          <select
            value={languageFilter}
            onChange={(e) => {
              setLanguageFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Languages</option>
            <option value="javascript">JavaScript</option>
            <option value="typescript">TypeScript</option>
            <option value="python">Python</option>
            <option value="java">Java</option>
            <option value="html">HTML</option>
            <option value="css">CSS</option>
            <option value="sql">SQL</option>
            <option value="c">C</option>
            <option value="cpp">C++</option>
            <option value="csharp">C#</option>
            <option value="go">Go</option>
            <option value="rust">Rust</option>
            <option value="ruby">Ruby</option>
            <option value="php">PHP</option>
            <option value="swift">Swift</option>
            <option value="kotlin">Kotlin</option>
          </select>

          <select
            value={difficultyFilter}
            onChange={(e) => {
              setDifficultyFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Difficulties</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Status</option>
            <option value="true">Published</option>
            <option value="false">Unpublished</option>
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}>Per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '8px 12px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: 'white'
              }}
            >
              <option value="6">6</option>
              <option value="12">12</option>
              <option value="24">24</option>
              <option value="48">48</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            Loading snippets...
          </div>
        ) : snippets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {searchQuery || languageFilter || difficultyFilter || statusFilter ? '🔍' : '💻'}
            </div>
            <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px' }}>
              {searchQuery || languageFilter || difficultyFilter || statusFilter
                ? 'No code snippets found matching your filters'
                : 'No code snippets yet'}
            </p>
            <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
              {searchQuery || languageFilter || difficultyFilter || statusFilter
                ? 'Try adjusting your search or filters'
                : 'Create your first code snippet to get started'}
            </p>
            {(searchQuery || languageFilter || difficultyFilter || statusFilter) ? (
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSearchQuery('');
                  setLanguageFilter('');
                  setDifficultyFilter('');
                  setStatusFilter('');
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </button>
            ) : (
              <button className="btn btn-primary" onClick={openCreateModal}>
                🚀 Create Snippet
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {snippets.map((snippet) => (
              <div
                key={snippet._id}
                style={{
                  padding: '16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                        {snippet.name}
                      </h3>
                      {snippet.snippetCode && (
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: '#f0f0f0',
                          color: '#333',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px',
                          border: '1px solid #ddd'
                        }}>
                          #{snippet.snippetCode}
                        </span>
                      )}
                    </div>
                    {snippet.description && (
                      <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px' }}>
                        {snippet.description.length > 150
                          ? snippet.description.substring(0, 150) + '...'
                          : snippet.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: difficultyColors[snippet.difficulty],
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        {snippet.difficulty}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        {snippet.programmingLanguage}
                      </span>
                      {!snippet.isPublished && (
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: '#fbbf24',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          Draft
                        </span>
                      )}
                      {snippet.tags && snippet.tags.length > 0 && snippet.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} style={{
                          padding: '2px 8px',
                          backgroundColor: '#e6fffa',
                          color: '#234e52',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                    <p style={{ color: '#4a5568', fontSize: '12px', margin: 0 }}>
                      📝 {snippet.explanations?.length || 0} explanations
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '16px' }}>
                    <button
                      onClick={() => handleViewSnippet(snippet)}
                      className="btn btn-primary"
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEditSnippet(snippet)}
                      className="btn btn-secondary"
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRegenerateExplanations(snippet._id)}
                      className="btn"
                      style={{ 
                        fontSize: '12px', 
                        padding: '6px 12px',
                        backgroundColor: '#ed8936',
                        color: 'white'
                      }}
                      title="Regenerate AI Explanations"
                    >
                      ✨ Regen
                    </button>
                    <button
                      onClick={() => {
                        setDeletingSnippet(snippet);
                        setDeleteDialogOpen(true);
                      }}
                      className="btn"
                      style={{ 
                        fontSize: '12px', 
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && pagination && pagination.totalPages > 1 && (
          <div style={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '13px', color: '#718096' }}>
              Showing {snippets.length} of {pagination.totalCount} snippets
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '8px 12px' }}
              >
                ← Previous
              </button>

              <div style={{ display: 'flex', gap: '4px' }}>
                {[...Array(pagination.totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  const showPage = pageNum === 1 ||
                    pageNum === pagination.totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                  if (!showPage) {
                    if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum} style={{ padding: '8px 4px', color: '#cbd5e0' }}>...</span>;
                    }
                    return null;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={currentPage === pageNum ? 'btn btn-primary' : 'btn btn-secondary'}
                      style={{ fontSize: '13px', padding: '8px 12px' }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '8px 12px' }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {editorOpen && (
        <CodeSnippetEditor
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setEditingSnippet(null);
          }}
          onSave={editorMode === 'create' ? handleCreateSnippet : handleUpdateSnippet}
          snippet={editingSnippet}
          mode={editorMode}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <h3 style={{ margin: 0, fontSize: '18px' }}>Confirm Delete</h3>
          </DialogTitle>
          <DialogContent>
            <p style={{ color: '#718096', fontSize: '14px', margin: '16px 0' }}>
              Are you sure you want to delete "<strong>{deletingSnippet?.name}</strong>"? This action cannot be undone.
            </p>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <button
              onClick={() => setDeleteDialogOpen(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSnippet}
              className="btn"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              Delete
            </button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default CodeSnippets;
